import React, { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import useCartCost from '../hooks/useCartCost';
import useCheckoutState from '../hooks/useCheckoutState';
import getStripeAccount from '../lib/getStripeAccount';
import getUpsellFunnelStep from '../lib/getUpsellFunnelStep';
import { getMinimumChargeAmount } from '../services/currency';
import { PAGE_TYPES } from '../enums/page-types';
import getStripe from '../lib/get-stripe';
import {
  buildFontFamilyWithFallbacks,
  generateGoogleFontsUrl,
  convertFontSizeForStripe,
} from '@repo/checkout/lib/fontUtils';
import { get, uniqueId } from 'lodash';
import { getFontBodySizes, getRadius, INPUT_PLACEHOLDER_COLOR } from './CheckoutCSS';
import useEffectiveColorMode from '../hooks/useEffectiveColorMode';

const defaultColor = {
  light: {
    textPrimary: '#231c1e',
    textSecondary: '#7f6d72',
    colorPrimary: '#000000',
    colorSecondary: '#000000',
    inputBorderColor: '#f0eeee',
    inputBorderColorFocus: '#f0eeee',
    inputBackgroundColor: '#ffffff',
    inputColor: '#1f2937',
    labelColor: '#000000',
    placeholderColor: INPUT_PLACEHOLDER_COLOR.light,
    tabBorderColor: '#e0d9dd',
  },
  dark: {
    textPrimary: '#f5f5f5',
    textSecondary: '#d4d4d4',
    colorPrimary: '#f5f5f5',
    colorSecondary: '#f5f5f5',
    inputBorderColor: '#525252',
    inputBorderColorFocus: '#737373',
    inputBackgroundColor: '#262626',
    inputColor: '#f5f5f5',
    labelColor: '#f5f5f5',
    placeholderColor: INPUT_PLACEHOLDER_COLOR.dark,
    tabBorderColor: '#525252',
  },
};

const getInputShadowStyle = shadowSetting => {
  const shadowStyleMap = {
    none: 'none',
    sm: `0 1px 2px 0 rgba(0, 0, 0, 0.05)`,
    md: `0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)`,
    lg: `0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)`,
    xl: `0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)`,
  };
  return shadowStyleMap[shadowSetting] || shadowStyleMap.sm;
};

export const paymentElementAppearance = (theme, colorMode) => {
  const fontMain = theme?.tokens?.fontMain;
  const fontBody = theme?.tokens?.fontBody;
  const effectiveBodyFamily = fontBody?.family || fontMain?.family;
  const textPrimary =
    theme?.tokens?.textPrimary?.[colorMode] || defaultColor[colorMode].textPrimary;
  const textSecondary =
    theme?.tokens?.textSecondary?.[colorMode] || defaultColor[colorMode].textSecondary;
  const colorPrimary =
    theme?.tokens?.colorPrimary?.[colorMode] || defaultColor[colorMode].colorPrimary;
  const colorSecondary =
    theme?.tokens?.colorSecondary?.[colorMode] || defaultColor[colorMode].colorSecondary;

  const fontBodyWeight = fontBody?.weight || 400;

  const formFieldLabelStyles = get(theme.elements, `checkout.form.fields.label.styles`);
  const formFieldLabelFocusStyles = get(theme.elements, `checkout.form.fields.label.focusStyles`);
  const formFieldInputStyles = get(theme.elements, `checkout.form.fields.input.styles`);

  const cardStyles = get(theme.elements, `checkout.cards.styles`);
  const cardSelectedStyles = get(theme.elements, `checkout.cards.selectedStyles`);
  const cardNameStyles = get(theme.elements, `checkout.cards.name.styles`);

  const formFieldInputPlaceholder = get(theme.elements, `checkout.form.fields.input.placeholder`);
  const formFieldInputPlaceholderStyles = get(
    theme.elements,
    `checkout.form.fields.input.placeholderStyles`,
  );

  const placeholderEnabled =
    typeof formFieldInputPlaceholder?.enabled === 'boolean'
      ? formFieldInputPlaceholder.enabled
      : true;

  // Get focus styles from theme if applicable
  const formFieldInputFocusStyles = get(theme.elements, `checkout.form.fields.input.focusStyles`);

  const fontBodySizes = getFontBodySizes(theme?.tokens);
  const radius = getRadius(theme?.tokens);

  // Stripe Elements renders inside a Stripe-hosted iframe, so our SSR-emitted
  // "<Family> Fallback" @font-face rule isn't reachable there. Opt out of the
  // adjusted fallback to avoid a dead entry in the font-family string.
  const stripeFontOptions = { includeAdjustedFallback: false };
  const baseFontFamily = buildFontFamilyWithFallbacks(effectiveBodyFamily, [], stripeFontOptions);
  const inputFontFamily = buildFontFamilyWithFallbacks(
    formFieldInputStyles?.fontFamily || effectiveBodyFamily,
    [],
    stripeFontOptions,
  );
  const labelFontFamily = buildFontFamilyWithFallbacks(
    formFieldLabelStyles?.fontFamily || effectiveBodyFamily,
    [],
    stripeFontOptions,
  );

  // Calculate focus ring color (similar to CheckoutCSS.js logic)
  const focusRingColor =
    formFieldInputFocusStyles?.borderColor?.[colorMode] ||
    formFieldInputStyles?.borderColor?.[colorMode] ||
    defaultColor[colorMode].inputBorderColorFocus;

  const inputFontSize = convertFontSizeForStripe(
    formFieldInputStyles?.fontSize || fontBodySizes.base?.fontSize,
  );
  const labelFontSize = convertFontSizeForStripe(
    formFieldLabelStyles?.fontSize || fontBodySizes.base?.fontSize,
  );

  const inputBoxShadow = getInputShadowStyle(formFieldInputStyles?.boxShadow);

  // note: it seems like only some font weights are supported by Stripe. it's not clear from the docs which ones are supported. if a font weight is not available, it seems to default to the closest available.

  const padding = `${formFieldInputStyles?.paddingY ? `${formFieldInputStyles?.paddingY}px` : '0.675rem'} ${formFieldInputStyles?.paddingX ? `${formFieldInputStyles?.paddingX}px` : '0.675rem'}`;

  // Cascade: field-specific color > textPrimary > hardcoded default
  // Theme defaults populate label/input colors with a snapshot of textPrimary at save time.
  // When Main text color changes via overrides, those snapshots become stale.
  // Only use the field-specific color if it differs from textPrimary (i.e. explicitly customized).
  const labelColor = formFieldLabelStyles?.color?.[colorMode];
  const effectiveLabelColor = labelColor && labelColor !== textPrimary ? labelColor : textPrimary;
  const inputColor = formFieldInputStyles?.color?.[colorMode];
  const effectiveInputColor = inputColor && inputColor !== textPrimary ? inputColor : textPrimary;

  const paymentElementAppearanceObject = {
    variables: {
      borderColor: colorSecondary,
      colorPrimary: textPrimary,
      colorText: textPrimary,
      colorBackground:
        formFieldInputStyles?.backgroundColor?.[colorMode] ||
        defaultColor[colorMode].inputBackgroundColor,
      fontFamily: baseFontFamily,
      fontSizeBase: convertFontSizeForStripe(fontBodySizes.base?.fontSize),
      fontSmooth: 'always',
      fontWeightLight: fontBodyWeight - 100,
      fontWeightNormal: fontBodyWeight,
      fontWeightMedium: fontBodyWeight + 100,
      fontWeightBold: fontBodyWeight + 200,
      padding: padding,
    },
    rules: {
      '.Label': {
        color: effectiveLabelColor,
        fontWeight: formFieldLabelStyles?.fontWeight || fontBodyWeight + 100 || 500,
        fontSize: labelFontSize,
        fontFamily: labelFontFamily,
        // font style is not supported in stripe elements
        marginBottom: '4px',
        paddingBottom:
          formFieldLabelStyles?.paddingBottom !== '' ||
          formFieldLabelStyles?.paddingBottom !== undefined
            ? `${formFieldLabelStyles?.paddingBottom}px`
            : '4px',
        marginTop: '4px', // combined with padding, this creates the gap between the fields
      },
      '.Label--focused': {
        color: formFieldLabelFocusStyles?.color?.[colorMode] || effectiveLabelColor,
      },
      '.Input': {
        border: `${formFieldInputStyles?.borderWidth ? `${formFieldInputStyles?.borderWidth}px` : '1px'} ${formFieldInputStyles?.borderStyle || 'solid'} ${formFieldInputStyles?.borderColor?.[colorMode] || defaultColor[colorMode].inputBorderColor}`,
        borderRadius: `${formFieldInputStyles?.borderRadius ? `${formFieldInputStyles?.borderRadius}px` : radius.input}`,
        backgroundColor:
          formFieldInputStyles?.backgroundColor?.[colorMode] ||
          defaultColor[colorMode].inputBackgroundColor,
        color: effectiveInputColor,
        fontWeight: formFieldInputStyles?.fontWeight || fontBodyWeight || 400,
        fontStyle: formFieldInputStyles?.fontStyle || 'normal',
        fontFamily: inputFontFamily,
        fontSize: inputFontSize,
        boxShadow: inputBoxShadow,
        transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
        padding: padding,
      },
      '.Input::placeholder': {
        color: placeholderEnabled
          ? formFieldInputPlaceholderStyles?.color?.[colorMode] ||
            defaultColor[colorMode].placeholderColor
          : 'transparent',
        fontFamily: inputFontFamily,
        fontSize: inputFontSize,
        fontWeight: formFieldInputStyles?.fontWeight || fontBodyWeight || 400,
        fontStyle: formFieldInputStyles?.fontStyle || 'normal',
      },
      '.Input:focus': {
        color: formFieldInputFocusStyles?.color?.[colorMode] || effectiveInputColor,
        backgroundColor:
          formFieldInputFocusStyles?.backgroundColor?.[colorMode] ||
          formFieldInputStyles?.backgroundColor?.[colorMode] ||
          defaultColor[colorMode].inputBackgroundColor,
        borderColor:
          formFieldInputFocusStyles?.borderColor?.[colorMode] ||
          defaultColor[colorMode].inputBorderColorFocus,
        boxShadow:
          formFieldInputStyles?.boxShadow === 'none'
            ? `0 0 0 3px ${focusRingColor}`
            : `0 0 0 3px ${focusRingColor}, ${inputBoxShadow}`,
        outline: 'none',
      },
      '.Tab': {
        borderColor: cardStyles?.borderColor?.[colorMode] || defaultColor[colorMode].tabBorderColor,
        backgroundColor:
          cardStyles?.backgroundColor?.[colorMode] || defaultColor[colorMode].inputBackgroundColor,
        borderWidth: cardStyles?.borderWidth ? `${cardStyles.borderWidth}px` : '1px',
        borderRadius: cardStyles?.borderRadius ? `${cardStyles.borderRadius}px` : radius.card,
        color: cardNameStyles?.color?.[colorMode] || textPrimary,
        //marginBottom: '4px', // combined with padding, this creates the gap between the fields
      },
      '.Tab:hover': {
        borderColor: cardStyles?.borderColor?.[colorMode] || defaultColor[colorMode].tabBorderColor,
        backgroundColor:
          cardStyles?.backgroundColor?.[colorMode] || defaultColor[colorMode].inputBackgroundColor,
        color: cardNameStyles?.color?.[colorMode] || textPrimary,
      },
      '.Tab--selected': {
        borderColor: cardSelectedStyles?.borderColor?.[colorMode] || colorSecondary,
        backgroundColor:
          cardSelectedStyles?.backgroundColor?.[colorMode] ||
          defaultColor[colorMode].inputBackgroundColor,
        borderWidth: cardStyles?.borderWidth ? `${cardStyles.borderWidth}px` : '1px',
        boxShadow: `0 0 0 1px ${cardSelectedStyles?.borderColor?.[colorMode] || colorSecondary}`,
        color: cardNameStyles?.color?.[colorMode] || textPrimary,
      },
      '.Tab--selected:hover': {
        borderColor: cardSelectedStyles?.borderColor?.[colorMode] || colorSecondary,
        backgroundColor:
          cardSelectedStyles?.backgroundColor?.[colorMode] ||
          defaultColor[colorMode].inputBackgroundColor,
        color: cardNameStyles?.color?.[colorMode] || textPrimary,
      },
      '.Tab:focus': {
        boxShadow: 'none',
        borderWidth: cardStyles?.borderWidth ? `${cardStyles.borderWidth}px` : '1px',
        borderColor: cardSelectedStyles?.borderColor?.[colorMode] || colorSecondary,
      },
      '.Tab--selected:focus': {
        boxShadow: `0 0 0 1px ${cardSelectedStyles?.borderColor?.[colorMode] || colorSecondary}`,
        borderWidth: cardStyles?.borderWidth ? `${cardStyles.borderWidth}px` : '1px',
        borderColor: cardSelectedStyles?.borderColor?.[colorMode] || colorSecondary,
      },
      '.TermsText': {
        color: textSecondary,
      },
    },
  };

  return paymentElementAppearanceObject;
};

function getFontUrl({ family, weight }) {
  if (!family) {
    return null;
  }

  return generateGoogleFontsUrl([{ family, weight }]).replace('&display=swap', '');
}

function getStripeElementOptions({
  pageType,
  totalDueAmount,
  currency,
  mode,
  appearance,
  fonts,
  shouldSavePaymentMethod,
}) {
  if (pageType === PAGE_TYPES.FORM) {
    return {
      appearance,
      fonts,
    };
  }

  // if totalDueAmount is not a number, use minimum charge amount
  // multiply by 2 to ensure that the total amount is at least the minimum charge amount
  // https://docs.stripe.com/currencies#minimum-and-maximum-charge-amounts
  // allow 0 for subscriptions (free trial or coupons)
  const minimumChargeAmount = getMinimumChargeAmount(currency);
  const amount =
    totalDueAmount === 0 && mode === 'subscription'
      ? 0
      : Number(totalDueAmount) > minimumChargeAmount
        ? Number(totalDueAmount)
        : minimumChargeAmount * 2;

  return {
    appearance,
    fonts,
    amount: amount,
    currency: currency,
    mode: mode,
    ...(shouldSavePaymentMethod && { setupFutureUsage: 'off_session' }),
    paymentMethodOptions: {
      us_bank_account: {
        verification_method: 'instant',
      },
    },
  };
}

const StripeElementsProvider = ({ children }) => {
  const { isTestMode, seller, page, publicKey, publicKeyTest, theme } = useCheckoutState();

  const { totalDueAmount, currency, mode } = useCartCost();

  const stripeAccount = useMemo(() => getStripeAccount(seller), [seller]);

  // Only remount Stripe Elements when font families change (which require new cssSrc URLs).
  // Other font properties (size, weight, style) are handled via options.appearance updates.
  // Skip remounting entirely when PaymentElement isn't used (card-only checkouts don't need it).
  const usesPaymentElement = page.paymentMethods?.stripe?.multiple?.enabled === true;

  // Express Checkout (Apple/Google Pay) also needs payment context (amount, currency, mode)
  // in the Elements options, even when PaymentElement is disabled (card-only checkouts).
  const usesExpressCheckout = page.paymentMethods?.stripe?.agpay?.enabled === true;

  const shouldSavePaymentMethod = useMemo(() => {
    if (page.savePaymentMethod) {
      return true;
    }

    const hasUpsellFunnelStep = getUpsellFunnelStep(page);

    if (hasUpsellFunnelStep) {
      return true;
    }

    return false;
  }, [page]);

  const colorMode = useEffectiveColorMode();

  const stripeElementOptions = useMemo(() => {
    const fonts = [];

    const fontMain = theme?.tokens?.fontMain;
    const fontBody = theme?.tokens?.fontBody;
    const effectiveBodyFamily = fontBody?.family || fontMain?.family;

    if (effectiveBodyFamily) {
      fonts.push({
        cssSrc: getFontUrl({
          family: effectiveBodyFamily,
          weight: fontBody?.weight || fontMain?.weight,
        }),
      });
    }

    // Skip expensive appearance computation when PaymentElement isn't used.
    // Stripe's Elements provider still processes options changes internally,
    // which can cause jitter in CSS transitions during preview.
    // But Express Checkout (Apple/Google Pay) still needs payment context.
    if (!usesPaymentElement) {
      if (usesExpressCheckout) {
        return getStripeElementOptions({
          pageType: page.type,
          totalDueAmount,
          currency,
          mode,
          appearance: {},
          fonts,
          shouldSavePaymentMethod,
        });
      }
      return { fonts };
    }

    const formFieldInputStyles = theme?.elements?.checkout?.form?.fields?.input?.styles;
    if (formFieldInputStyles?.fontFamily) {
      fonts.push({
        cssSrc: getFontUrl({
          family: formFieldInputStyles?.fontFamily,
          weight: formFieldInputStyles?.fontWeight,
        }),
      });
    }

    const formFieldLabelStyles = theme?.elements?.checkout?.form?.fields?.label?.styles;
    if (formFieldLabelStyles?.fontFamily) {
      fonts.push({
        cssSrc: getFontUrl({
          family: formFieldLabelStyles?.fontFamily,
          weight: formFieldLabelStyles?.fontWeight,
        }),
      });
    }

    const appearance = paymentElementAppearance(theme, colorMode);

    return getStripeElementOptions({
      pageType: page.type,
      totalDueAmount,
      currency,
      mode,
      appearance,
      fonts,
      shouldSavePaymentMethod,
    });
  }, [
    page,
    totalDueAmount,
    currency,
    mode,
    shouldSavePaymentMethod,
    theme,
    colorMode,
    usesPaymentElement,
    usesExpressCheckout,
  ]);

  const themeKey = usesPaymentElement
    ? JSON.stringify({
        fontMainFamily: theme?.tokens?.fontMain?.family,
        fontBodyFamily: theme?.tokens?.fontBody?.family,
        inputFontFamily: theme?.elements?.checkout?.form?.fields?.input?.styles?.fontFamily,
        labelFontFamily: theme?.elements?.checkout?.form?.fields?.label?.styles?.fontFamily,
      })
    : 'stable';

  const key = useMemo(() => {
    // `mode` is part of the key: Stripe doesn't allow changing an Elements
    // group's mode via options updates, so switching between a one-time and a
    // recurring price (multi-price pickers) must remount Elements — otherwise
    // the confirm step runs against the wrong intent mode and the payment
    // fails.
    return uniqueId(`stripe-elements-provider-${themeKey}-${mode || 'none'}`);
  }, [themeKey, mode]);

  return (
    <Elements
      stripe={getStripe({ stripeAccount, isTestMode, publicKey, publicKeyTest })}
      options={stripeElementOptions}
      key={key}
    >
      {children}
    </Elements>
  );
};

export default StripeElementsProvider;
